import { useCallback } from 'react';
import { SCOPE_OF_ADVICE_TOKEN } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/unlayerDesignMutations';
import {
	buildNeedsAnalysisParagraph as buildNeedsAnalysisParagraphService,
	buildNeedsAnalysisTableHtml as buildNeedsAnalysisTableHtmlService,
	buildScopeOfAdviceTableHtml as buildScopeOfAdviceTableHtmlService,
} from '@/components/Drawers/AddUnlayerEmailDrawer/scopeOfAdviceTableBuilders';

/**
 * Builds the scope-of-advice HTML fragments and token replacements used when
 * loading templates that contain the scope-of-advice placeholder.
 */
export const useScopeOfAdviceInjectionOptions = ({
	classesOfRisk,
	isMarble,
	scopeOfAdviceClientEnvelope,
	scopeOfAdviceDealContext,
	userDetails,
}) => {
	const buildScopeOfAdviceTableHtml = useCallback(async () => {
		return buildScopeOfAdviceTableHtmlService({
			scopeOfAdviceDealContext,
			scopeOfAdviceClientEnvelope,
			classesOfRisk,
			userDetails,
			isMarble,
		});
	}, [
		classesOfRisk,
		isMarble,
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
	]);

	const buildNeedsAnalysisTableHtml = useCallback(async () => {
		return buildNeedsAnalysisTableHtmlService({
			scopeOfAdviceClientEnvelope,
			scopeOfAdviceDealContext,
			classesOfRisk,
			userDetails,
			isMarble,
		});
	}, [
		classesOfRisk,
		isMarble,
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
	]);

	const buildNeedsAnalysisParagraph = useCallback(() => {
		return buildNeedsAnalysisParagraphService({
			scopeOfAdviceClientEnvelope,
			scopeOfAdviceDealContext,
			classesOfRisk,
			userDetails,
			isMarble,
		});
	}, [
		classesOfRisk,
		isMarble,
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
	]);

	return useCallback(async () => {
		const [scopeTableResult, needsAnalysisTableResult, needsAnalysisParagraphResult] =
			await Promise.allSettled([
				buildScopeOfAdviceTableHtml(),
				buildNeedsAnalysisTableHtml(),
				buildNeedsAnalysisParagraph(),
			]);

		const scopeOfAdviceTableHtml =
			scopeTableResult.status === 'fulfilled' ? scopeTableResult.value : '';
		if (scopeOfAdviceTableHtml.length === 0) return {};

		const needsAnalysisTableHtml =
			needsAnalysisTableResult.status === 'fulfilled' ? needsAnalysisTableResult.value : '';
		const needsAnalysisParagraph =
			needsAnalysisParagraphResult.status === 'fulfilled'
				? needsAnalysisParagraphResult.value
				: '';

		return {
			tokenReplacements: {
				[SCOPE_OF_ADVICE_TOKEN]: scopeOfAdviceTableHtml,
			},
			scopeOfAdviceHtmlBlocks: [
				scopeOfAdviceTableHtml,
				needsAnalysisTableHtml,
				needsAnalysisParagraph,
			].filter((html) => typeof html === 'string' && html.length > 0),
		};
	}, [buildNeedsAnalysisParagraph, buildNeedsAnalysisTableHtml, buildScopeOfAdviceTableHtml]);
};
